import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPurchaseHistoryReport,
  exportPurchaseHistoryReport,
  clearError,
} from 'store/slices/reportSlice';
import { fetchAllInventorySubcategories, fetchInventoryCategories, fetchInventorySubcategories, fetchVendorDropdown } from 'store/slices/commonSlice';

import PageHeader from 'components/common/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { ShoppingCart, IndianRupee, Package, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import LogDateFilter from 'components/common/LogDateFilter';
import DataTable from 'components/common/DataTable';
import { Loader2 } from 'lucide-react';
import FormField from 'components/common/FormField';
import CategorySubcategorySelect from 'components/common/CategorySubcategorySelect';

const PurchaseHistoryPage = () => {
  const dispatch = useDispatch();

  const {
    purchaseHistoryReport = {},
    isLoading,
    error,
  } = useSelector((state) => state.report);

  const summary = purchaseHistoryReport?.data?.summary || {};
  const results = purchaseHistoryReport?.data?.results || [];
  const pagination = purchaseHistoryReport?.pagination || {};


  // Advanced filter states
  const [advancedFilters, setAdvancedFilters] = useState({
    vendor: 'all',
    category: 'all',
    sub_category: 'all',
    quantity_gte: '',
    quantity_lte: '',
    amount_gte: '',
    amount_lte: '',
  });

  // Get categories, subcategories, and vendors from Redux state
  const categories = useSelector((state) => state.common?.inventoryCategories.items || []);

  const subcategories = useSelector((state) => state.common?.allInventorySubcategories.items || []);

  const vendors = useSelector((state) => state.common?.vendors.items || []);


  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilters, setDateFilters] = useState({});
  const [isAdvancedFilterExpanded, setIsAdvancedFilterExpanded] = useState(false);


  // Calculate total pages from server response
  const totalPages = pagination.total_pages || 1;

  // Check if any advanced filters are active (excluding 'all' values)
  const hasActiveAdvancedFilters = Object.values(advancedFilters).some(value =>
    value !== '' && value !== 'all'
  );

  const refetch = useCallback(() => {
    // Build the API params object with all filters
    const apiParams = {
      page,
      page_size: pageSize,
      search: searchTerm,
      report_type: 'purchase_history',
      ...dateFilters,
      // Add advanced filters with proper naming for backend
      ...(advancedFilters.vendor !== 'all' && { vendor: advancedFilters.vendor }),
      ...(advancedFilters.category !== 'all' && { category: advancedFilters.category }),
      ...(advancedFilters.sub_category !== 'all' && { sub_category: advancedFilters.sub_category }),
      ...(advancedFilters.quantity_gte && { quantity__gte: advancedFilters.quantity_gte }),
      ...(advancedFilters.quantity_lte && { quantity__lte: advancedFilters.quantity_lte }),
      ...(advancedFilters.amount_gte && { amount__gte: advancedFilters.amount_gte }),
      ...(advancedFilters.amount_lte && { amount__lte: advancedFilters.amount_lte }),
    };

    dispatch(fetchPurchaseHistoryReport(apiParams));
  }, [dispatch, page, pageSize, searchTerm, dateFilters, advancedFilters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Reset to page 1 when search term, page size, or filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize, dateFilters, advancedFilters]);

  // Fetch categories and vendors on component mount
  useEffect(() => {
    dispatch(fetchInventoryCategories());
    dispatch(fetchAllInventorySubcategories());
    dispatch(fetchVendorDropdown());
  }, [dispatch]);

  // --- Event Handlers ---
  const handleAdvancedFilterChange = (key, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      vendor: 'all',
      category: 'all',
      sub_category: 'all',
      quantity_gte: '',
      quantity_lte: '',
      amount_gte: '',
      amount_lte: '',
    });
  };

  const columns = [
    { accessorKey: 'Date', header: 'Purchase Date' },
    { accessorKey: 'Vendor', header: 'Vendor' },
    { accessorKey: 'Category', header: 'Category' },
    { accessorKey: 'Sub Category', header: 'Sub Category' },
    { accessorKey: 'Brand Name', header: 'Brand' },
    { accessorKey: 'Model No', header: 'Model No' },
    { accessorKey: 'Serial No', header: 'Serial No' },
    {
      accessorKey: 'Quantity',
      header: 'Quantity',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.Quantity ?? 0}</span>
      ),
    },
    {
      accessorKey: 'Amount',
      header: 'Amount (₹)',
      cell: ({ row }) => (
        <span className="font-semibold text-green-600">
          ₹{row.original.Amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) ?? '0.00'}
        </span>
      ),
    },
    { accessorKey: 'Invoice', header: 'Invoice No' },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Purchase History Report"
        description="Complete record of all inventory purchases with vendor and cost details"
      />

      <LogDateFilter onFilter={(f) => setDateFilters(f)} />

      {/* Advanced Filters Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Advanced Filters
              {hasActiveAdvancedFilters && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </CardTitle>
            <div className="flex gap-2">
              {hasActiveAdvancedFilters && (
                <Button variant="outline" size="sm" onClick={resetAdvancedFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAdvancedFilterExpanded(!isAdvancedFilterExpanded)}
              >
                {isAdvancedFilterExpanded ? (
                  <>
                    Hide <ChevronUp className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Show <ChevronDown className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {isAdvancedFilterExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">


              <FormField
                label="Vendor"
                name="vendor"
                type="select"
                value={advancedFilters.vendor}
                onChange={(e) =>
                  handleAdvancedFilterChange('vendor', e.target.value)
                }
                options={[
                  { value: 'all', label: 'All' },
                  ...(Array.isArray(vendors)
                    ? vendors.map((vendor) => ({
                      value: String(vendor.id),
                      label: vendor.name,
                    }))
                    : []),
                ]}
              />

              {/* <FormField
                label="Category"
                name="category"
                type="select"
                value={advancedFilters.category}
                onChange={(e) =>
                  handleAdvancedFilterChange('category', e.target.value)
                }
                options={[
                  { value: 'all', label: 'All' },
                  ...(Array.isArray(categories)
                    ? categories.map((category) => ({
                      value: String(category.id),
                      label: category.name,
                    }))
                    : []),
                ]}
              />

              <FormField
                label="Sub Category"
                name="sub_category"
                type="select"
                value={advancedFilters.sub_category}
                onChange={(e) =>
                  handleAdvancedFilterChange('sub_category', e.target.value)
                }
                options={[
                  { value: 'all', label: 'All' },
                  ...(Array.isArray(subcategories)
                    ? subcategories.map((subcategory) => ({
                      value: String(subcategory.id),
                      label: subcategory.name,
                    }))
                    : []),
                ]}
              /> */}

              <CategorySubcategorySelect
                category={advancedFilters.category}
                subCategory={advancedFilters.sub_category}
                onCategoryChange={(value) => {
                  setAdvancedFilters((prev) => ({
                    ...prev,
                    category: value,
                    sub_category: "all",
                  }));
                }}
                onSubCategoryChange={(value) =>
                  setAdvancedFilters((prev) => ({
                    ...prev,
                    sub_category: value,
                  }))
                }
              />


              <div className="space-y-2">
                <Label>Quantity Range</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={advancedFilters.quantity_gte}
                    onChange={(e) => handleAdvancedFilterChange('quantity_gte', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={advancedFilters.quantity_lte}
                    onChange={(e) => handleAdvancedFilterChange('quantity_lte', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Amount Range (₹)</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={advancedFilters.amount_gte}
                    onChange={(e) => handleAdvancedFilterChange('amount_gte', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={advancedFilters.amount_lte}
                    onChange={(e) => handleAdvancedFilterChange('amount_lte', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Purchases</CardTitle>
            <ShoppingCart className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary['Total Purchases'] ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Quantity</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary['Total Quantity'] ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Amount Spent</CardTitle>
            <IndianRupee className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {summary['Total Amount'] || '₹0.00'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* {isLoading && results.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin mr-3" />
          <span className="text-lg">Loading purchase history...</span>
        </div>
      ) : ( */}
      <DataTable
        data={results} // Use the paginated results from the server
        columns={columns}
        totalCount={pagination.count}
        searchPlaceholder="Search by vendor, category, invoice, brand, model, serial..."
        onSearch={setSearchTerm}
        showSearch={true}
        pageIndex={page - 1} // Convert to 0-based index for DataTable
        pageSize={pageSize}
        pageCount={totalPages} // Use total pages from server response
        onPageChange={(p) => setPage(p + 1)} // Convert back to 1-based index
        onPageSizeChange={(newSize) => setPageSize(newSize)}
        isLoading={isLoading}
        onExport={() =>
          dispatch(exportPurchaseHistoryReport({
            filters: {
              page,
              page_size: pageSize,
              search: searchTerm,
              report_type: 'purchase_history',
              ...dateFilters,
              // Include advanced filters in export
              ...(advancedFilters.vendor !== 'all' && { vendor: advancedFilters.vendor }),
              ...(advancedFilters.category !== 'all' && { category: advancedFilters.category }),
              ...(advancedFilters.sub_category !== 'all' && { sub_category: advancedFilters.sub_category }),
              ...(advancedFilters.quantity_gte && { quantity__gte: advancedFilters.quantity_gte }),
              ...(advancedFilters.quantity_lte && { quantity__lte: advancedFilters.quantity_lte }),
              ...(advancedFilters.amount_gte && { amount__gte: advancedFilters.amount_gte }),
              ...(advancedFilters.amount_lte && { amount__lte: advancedFilters.amount_lte }),
            }
          }))
        }
        showExport={true}
        showImport={false}
      />
      {/* )} */}
    </div>
  );
};

export default PurchaseHistoryPage;